import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import md5 from 'md5';
import { toast } from 'react-toastify';
import header from '../../../assets/icm-header.jpg';
import {
  getBase64PDFImage,
  scaleStyles,
  baseStyles,
} from '../../../util/utils';
import {
  getPdfBase64,
  enviarArquivo,
  normalizeWhatsappPhone,
} from '../../../services/pdfWhatsapp';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Monta os blocos de texto do corpo aplicante a quebra de página dinâmica
 */
function processarCorpoTexto(corpoTexto, quebraPagina) {
  if (!corpoTexto) return [];

  // Se não foi solicitada nenhuma quebra dinâmica no meio do corpo
  if (quebraPagina === 'sem' || !quebraPagina) {
    return [
      {
        text: corpoTexto,
        style: 'observations',
        margin: [0, 0, 0, 5],
      },
    ];
  }

  // Identifica a palavra-chave de divisão
  const termoQuebra = quebraPagina === 'aorta' ? 'AORTA:' : quebraPagina === 'ducto' ? 'DUCTO ARTERIOSO:' : 'CONCLUSÃO:';
  const posDivisao = corpoTexto.indexOf(termoQuebra);

  // Se a tag escolhida não for encontrada na descrição, mantém o texto corrido
  if (posDivisao === -1) {
    return [
      {
        text: corpoTexto,
        style: 'observations',
        margin: [0, 0, 0, 5],
      },
    ];
  }

  const parte1 = corpoTexto.substring(0, posDivisao).trim();
  const parte2 = corpoTexto.substring(posDivisao).trim();

  return [
    {
      text: parte1,
      style: 'observations',
      margin: [0, 0, 0, 5],
    },
    {
      text: parte2,
      style: 'observations',
      pageBreak: 'before',
      margin: [0, 0, 0, 5],
    },
  ];
}

/**
 * Gera PDF avançado para relatório fetal, replicando layout React
 * @param {Object} fetal - Dados do exame fetal
 * @param {String} tipo - Tipo de ação (imprimirNormal, imprimirTimbrado, envioWpp)
 * @param {Number} fontScale - Escala de fonte
 * @param {String} quebraPagina - Onde forçar quebra: 'aorta' | 'ducto' | 'sem'
 */
export default async function generateFetalPDF(
  fetal,
  tipo,
  fontScale = 1.0,
  quebraPagina = 'sem'
) {
  const styles = scaleStyles(baseStyles, fontScale);
  const headerImage =
    tipo === 'imprimirTimbrado' || tipo === 'envioWpp'
      ? await getBase64PDFImage(header)
      : null;

  // Processa a divisão do corpo com base na opção selecionada
  const blocosCorpo = processarCorpoTexto(fetal.DESCRICAO, quebraPagina);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: headerImage ? [40, 140, 40, 60] : [40, 60, 40, 60],
    footer: (currentPage, pageCount) => {
      return {
        columns: [
          {
            text: `Página ${currentPage} de ${pageCount}`,
            alignment: 'center',
            style: 'footer',
          },
        ],
        margin: [0, 10, 0, 0],
      };
    },

    content: [
      {
        text: 'ECOCARDIOGRAMA FETAL',
        style: 'title',
        alignment: 'center',
        decoration: 'underline',
        margin: [0, 0, 0, 15],
      },
      // --- Cabeçalho com retângulo preto ---
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    columns: [
                      {
                        text: `Nome do Paciente: ${fetal.CODPAC} - ${fetal.NOMEPAC}`,
                        width: '100%',
                        bold: true,
                      },
                    ],
                    margin: [0, 0, 0, 5],
                  },
                  {
                    columns: [
                      { text: `Data Nasc: ${fetal.dataNasc}`, width: '33%' },
                      { text: `Sexo: ${fetal.SEXOPAC}`, width: '33%' },
                      { text: `Idade: ${fetal.idadePAC} anos`, width: '33%' },
                    ],
                    margin: [0, 0, 0, 5],
                  },
                  {
                    columns: [
                      { text: `Convênio: ${fetal.SIGLACONV}`, width: '33%' },
                      { text: `Código: ${fetal.CODFETAL}`, width: '33%' },
                      {
                        text: `Data do Laudo: ${fetal.dataFetal}`,
                        width: '33%',
                      },
                    ],
                    margin: [0, 0, 0, 10],
                  },
                  {
                    columns: [
                      {
                        text: `Médico que Solicitou: Dr. ${fetal.Solicitou}`,
                        width: '100%',
                      },
                    ],
                    margin: [0, 0, 0, 0],
                  },
                ],
                margin: [10, 5, 10, 5],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => 'black',
          vLineColor: () => 'black',
        },
        margin: [0, 10, 0, 10],
      },

      // --- Inserção do Corpo (dinâmico com ou sem quebra manual) ---
      ...blocosCorpo,

      // --- Arquivos disponíveis ---
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: `Imagens disponíveis no site: www.icm.com.br (Resultado de Exames)\nCódigo de verificação: ${fetal.CODPAC}`,
                style: 'files',
                alignment: 'center',
                margin: [5, 5, 5, 5],
              },
            ],
          ],
        },
        layout: {
          hLineColor: '#999',
          vLineColor: '#999',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 15, 0, 15],
      },
      // --- Assinatura do médico ---
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    text: 'Conferido e assinado eletronicamente por:',
                    italics: true,
                    alignment: 'center',
                  },
                  {
                    text: `Dr. ${fetal.Realizou}`,
                    bold: true,
                    alignment: 'center',
                  },
                  {
                    text: `CRM: ${fetal.CRMMED} - RQE: ${fetal?.RQE ?? ''}`,
                    bold: true,
                    style: 'small',
                    alignment: 'center',
                  },
                  {
                    text: `Assinatura Digital: ${md5(
                      JSON.stringify(fetal.DESCRICAO || '')
                    )}`,
                    italics: true,
                    alignment: 'center',
                    style: 'smaller',
                  },
                  {
                    text: [
                      { text: 'Emitido em: ', bold: true },
                      `${new Date().toLocaleString('pt-BR')}\n`,
                      {
                        text: 'ICM - Instituto do Coração de Marília',
                        bold: true,
                      },
                    ],
                    style: 'smaller',
                    color: '#444',
                    alignment: 'center',
                  },
                ],
                margin: [10, 8, 10, 8],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: '#999',
          vLineColor: '#999',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 10, 0, 0],
      },
    ],
    styles,
  };

  if (headerImage) {
    docDefinition.header = {
      image: headerImage,
      width: 500,
      alignment: 'center',
      margin: [0, 10, 0, 0],
    };
  }

  if (tipo === 'imprimirNormal' || tipo === 'imprimirTimbrado') {
    pdfMake.createPdf(docDefinition).open();
  } else {
    const celular = normalizeWhatsappPhone(fetal?.CELULARPAC);
    if (!celular || celular.length < 10) {
      toast.warn('Número de celular não encontrado ou inválido!');
      return;
    }

    if (!fetal?.CODPAC || !fetal?.NOMEPAC) {
      toast.error('Dados do paciente incompletos!');
      return;
    }

    try {
      const pdfGenerator = pdfMake.createPdf(docDefinition);
      const pdfBase64 = await getPdfBase64(pdfGenerator);
      const data = await enviarArquivo({
        to: celular,
        pdfBase64,
        paciente: {
          id: fetal?.CODPAC,
          nome: fetal?.NOMEPAC,
          medico: fetal?.Realizou,
        },
        codLaudo: fetal?.CODFETAL,
        fileName: `Laudo_Fetal_${fetal?.CODPAC}.pdf`,
        caption: `Olá *${fetal?.NOMEPAC}*, tudo bem? 😊
  Segue em anexo o seu *Laudo de Ecocardiograma Fetal*.

  As *imagens do exame* podem ser acessadas pelo link:
  👉 http://www.icm.com.br (Resultados de Exames)

  Qualquer dúvida, nossa equipe estará à disposição no setor de atendimento pelo WhatsApp 14-3402-5252\n
  ICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'fetal',
          documentTitle: 'Laudo de Ecocardiograma Fetal',
        },
      });

      toast.success(data.message);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        'Erro ao enviar laudo via WhatsApp';
      toast.error(errorMessage);
    }
  }

  return docDefinition;
}